import { useForm } from "@tanstack/react-form";
import {
	createFileRoute,
	useNavigate,
	useRouter,
	useSearch,
} from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { usePostHog } from "posthog-js/react";
import { commitLocalUpdate } from "react-relay";
import { toast } from "sonner";
import { z } from "zod";
import { trackLogin } from "@/analytics/auth";
import { Heading } from "@/components/heading";
import { Icon } from "@/components/icon";
import { Text, TextLink } from "@/components/text";
import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { login as loginHandler } from "@/handlers/login";
import { environment } from "@/relay/environment";

export const Route = createFileRoute("/_auth/(no-auth)/login")({
	component: RouteComponent,
	validateSearch: z.object({
		redirectTo: z.string().optional(),
	}),
	loaderDeps: ({ search }) => [search.redirectTo],
	head: () => {
		return {
			meta: [
				{
					title: "Entrar | Scarf - Área de Membros",
				},
				{
					name: "description",
					content:
						"Faça login na Scarf para acessar sua área de membros, gerenciar cursos, mentorias e sua comunidade de forma simples e eficiente.",
				},
				{
					name: "keywords",
					content:
						"login scarf, entrar scarf, área de membros, comunidade, cursos online, mentorias, plataforma de cursos",
				},
				{
					property: "og:title",
					content: "Entrar | Scarf - Área de Membros",
				},
				{
					property: "og:description",
					content:
						"Faça login na Scarf para acessar sua área de membros, gerenciar cursos, mentorias e sua comunidade de forma simples e eficiente.",
				},
				{
					property: "og:url",
					content: "https://painel.scarf.club/login",
				},
				{
					property: "og:type",
					content: "website",
				},
				{
					property: "twitter:title",
					content: "Entrar | Scarf - Área de Membros",
				},
				{
					property: "twitter:description",
					content:
						"Faça login na Scarf para acessar sua área de membros, gerenciar cursos, mentorias e sua comunidade de forma simples e eficiente.",
				},
				{
					property: "twitter:url",
					content: "https://painel.scarf.club/login",
				},
			],
		};
	},
});

function RouteComponent() {
	const { redirectTo } = useSearch({ from: Route.id });
	const navigate = useNavigate({ from: Route.fullPath });
	const router = useRouter();
	const posthog = usePostHog();

	const login = useServerFn(loginHandler);
	const form = useForm({
		defaultValues: {
			username: "",
			password: "",
		},
		validators: {
			onChange: z.object({
				username: z.string().email("Email inválido"),
				password: z.string().min(1, "A senha é obrigatória"),
			}),
		},
		onSubmit: async ({ value }) => {
			return await login({
				data: { ...value, as: (window as Window & { as?: string }).as },
			})
				.then(async (data) => {
					commitLocalUpdate(environment, (store) => store.invalidateStore());

					if (data?.user) {
						trackLogin(posthog, data.user);
					}

					await router.invalidate();

					if (data) {
						navigate({
							to: redirectTo || "/",
						});
					}
				})
				.catch((err) => {
					posthog.capture("auth_login_failed", {
						error_type: err.message?.includes("not found")
							? "not_found"
							: err.message?.includes("credentials")
								? "invalid_credentials"
								: "unknown",
					});

					if (err.message?.includes("not found")) {
						toast.error(
							"Não encontramos uma conta com esse email. Faça seu cadastro para continuar.",
						);
					} else if (err.message?.includes("credentials")) {
						toast.error("Usuário ou senha inválidos");
					} else {
						toast.error(
							"Não foi possível fazer login. Verifique os dados inseridos e tente novamente.",
						);
					}

					throw err;
				});
		},
	});

	return (
		<div className="flex justify-center w-full">
			<div className="flex w-full flex-col gap-8 justify-center p-6 max-w-[550px]">
				<Heading>Bem-vindo de Volta!</Heading>

				<form
					className="flex flex-col gap-8"
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
				>
					<form.Field name="username">
						{(field) => (
							<Field>
								<FieldLabel htmlFor={field.name}>Email</FieldLabel>
								<Input
									id={field.name}
									name={field.name}
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									type="email"
									placeholder="Email"
									aria-invalid={
										!!(field.state.meta.isTouched && field.state.meta.errors[0])
									}
								/>

								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</Field>
						)}
					</form.Field>

					<form.Field name="password">
						{(field) => (
							<Field>
								<FieldLabel htmlFor={field.name}>Senha</FieldLabel>
								<Input
									id={field.name}
									name={field.name}
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									type="password"
									placeholder="Senha"
									aria-invalid={
										!!(field.state.meta.isTouched && field.state.meta.errors[0])
									}
								/>

								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</Field>
						)}
					</form.Field>

					<TextLink to="/esqueci-a-senha">Esqueci a senha</TextLink>

					<form.Subscribe
						selector={(state) => [state.isSubmitting, state.isSubmitSuccessful]}
					>
						{([isSubmitting, isSubmitSuccessful]) => (
							<div className="flex flex-col gap-6">
								<Button
									type="submit"
									color={isSubmitSuccessful ? "emerald" : "primary"}
									disabled={isSubmitting || isSubmitSuccessful}
									className="flex items-center justify-center gap-2"
								>
									{isSubmitSuccessful ? (
										<Icon name="CircleCheck" className="size-4" />
									) : null}

									{!isSubmitSuccessful && (
										<>Entrar {isSubmitting ? <Spinner /> : null}</>
									)}
								</Button>

								<Text className="font-medium">
									Ainda não tem uma conta?{" "}
									<TextLink to="/cadastro" disabled={isSubmitting}>
										Crie agora
									</TextLink>
								</Text>
							</div>
						)}
					</form.Subscribe>
				</form>
			</div>
		</div>
	);
}
